import { toast } from "sonner";
import { fetchFastApi } from "../../api/fastapi";
import {
  attributesValidator,
  valueGroupValidator,
} from "../../models/schemasV2";
import {
  HandleAttributeValueGroupActionParams,
  HandleValueGroupActionParams,
  Template,
} from "./types";

export const handleCreateValueGroup = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  { valueGroupName, description }: HandleValueGroupActionParams,
) => {
  if (!valueGroupName || !description) {
    toast.error("Please fill in all fields");
    return;
  }

  const valueGroupId = crypto.randomUUID();

  const newValueGroup = valueGroupValidator.parse({
    id: valueGroupId,
    name: valueGroupName,
    sourceDescription: description,
    attributes: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      valueGroups: [...prevTemplate.valueGroups, newValueGroup],
    };
  });

  try {
    await fetchFastApi({
      path: `/schemas/v2/schema/${templateId}/add_value_group`,
      method: "POST",
      body: {
        value_group: newValueGroup,
      },
    });
  } catch (error) {
    toast.error("An error occurred while creating value group");
    console.error("Error creating value group:", error);
    // Revert optimistic update
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      return {
        ...prevTemplate,
        valueGroups: prevTemplate.valueGroups.filter(
          (valueGroup) => valueGroup.id !== valueGroupId,
        ),
      };
    });
  }
};

export const handleDeleteValueGroup = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  valueGroupId: string,
) => {
  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      valueGroups: prevTemplate.valueGroups.filter(
        (valueGroup) => valueGroup.id !== valueGroupId,
      ),
    };
  });

  try {
    await fetchFastApi({
      path: `/schemas/v2/schema/${templateId}/value_group/${valueGroupId}`,
      method: "DELETE",
    });
  } catch (error) {
    toast.error("An error occurred while deleting value group");
    console.error("Error deleting value group:", error);
  }
};

export const handleAttributeValueGroupAction = async (
  templateId: string,
  setTemplate: React.Dispatch<React.SetStateAction<Template | null>>,
  {
    valueGroupId,
    attributeId,
    attributeName,
    attributeDescription,
    attributeType,
    inferred,
    unit,
    presets,
    required,
    deleteAttribute,
  }: HandleAttributeValueGroupActionParams,
) => {
  if (!valueGroupId || !attributeName) {
    toast.error("Please fill in all fields");
    return;
  }

  const isNewAttribute = !attributeId;
  const actionAttributeId = attributeId || crypto.randomUUID();

  const attribute = attributesValidator.parse({
    id: actionAttributeId,
    name: attributeName,
    tableId: "",
    valueGroupId: valueGroupId,
    description: attributeDescription,
    type: attributeType,
    baseUnit: unit,
    required,
    enum: presets,
    inferred,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Optimistic update
  setTemplate((prevTemplate) => {
    if (!prevTemplate) return null;
    return {
      ...prevTemplate,
      valueGroups: prevTemplate.valueGroups.map((valueGroup) => {
        if (valueGroup.id === valueGroupId) {
          if (deleteAttribute) {
            return {
              ...valueGroup,
              attributes: valueGroup.attributes.filter(
                (attr) => attr.id !== actionAttributeId,
              ),
            };
          } else if (isNewAttribute) {
            return {
              ...valueGroup,
              attributes: [...valueGroup.attributes, attribute],
            };
          } else {
            return {
              ...valueGroup,
              attributes: valueGroup.attributes.map((attr) =>
                attr.id === actionAttributeId ? attribute : attr,
              ),
            };
          }
        }
        return valueGroup;
      }),
    };
  });

  try {
    let apiPath = `/schemas/v2/schema/${templateId}/value_group/${valueGroupId}`;
    let method: "POST" | "PUT" | "DELETE" = "POST";
    let body: { attribute: typeof attribute } | undefined = undefined;

    if (deleteAttribute) {
      apiPath += `/attribute/${actionAttributeId}`;
      method = "DELETE";
    } else if (isNewAttribute) {
      apiPath += "/add_attribute";
      method = "POST";
      body = { attribute };
    } else {
      apiPath += `/attribute/${actionAttributeId}`;
      method = "PUT";
      body = { attribute };
    }

    await fetchFastApi({
      path: apiPath,
      method,
      body,
    });
  } catch (error) {
    toast.error(
      `An error occurred while ${
        isNewAttribute ? "adding" : "editing"
      } attribute in value group`,
    );
    console.error(
      `Error ${
        isNewAttribute ? "adding" : "editing"
      } attribute in value group:`,
      error,
    );
    // Revert optimistic update
    setTemplate((prevTemplate) => {
      if (!prevTemplate) return null;
      return {
        ...prevTemplate,
        valueGroups: prevTemplate.valueGroups.map((valueGroup) => {
          if (valueGroup.id === valueGroupId) {
            if (deleteAttribute) {
              return {
                ...valueGroup,
                attributes: [...valueGroup.attributes, attribute],
              };
            } else if (isNewAttribute) {
              return {
                ...valueGroup,
                attributes: valueGroup.attributes.filter(
                  (attr) => attr.id !== actionAttributeId,
                ),
              };
            } else {
              return {
                ...valueGroup,
                attributes: valueGroup.attributes.map((attr) =>
                  attr.id === actionAttributeId
                    ? {
                        ...attr,
                        name: attributeName,
                        description: attributeDescription,
                        type: attributeType,
                        baseUnit: unit,
                        required,
                        enum: presets || null,
                      }
                    : attr,
                ),
              };
            }
          }
          return valueGroup;
        }),
      };
    });
  }
};
